import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { deriveKeyFromPin, decrypt, zeroize } from '@gonka-wallet/crypto';
import { Button } from '@gonka-wallet/ui-tokens';
import { PinPad } from '../components/PinPad.js';
import { MnemonicGrid } from '../components/MnemonicGrid.js';
import { useWalletStore } from '../stores/walletStore.js';
import { useToastStore } from '../stores/toastStore.js';
import { hexToBytes } from '../lib/format.js';
import { copySecretWithAutoClear } from '../lib/clipboard.js';

const AUTO_HIDE_SECONDS = 60;

export function ShowMnemonic() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const wallets = useWalletStore((s) => s.wallets);
  const pushToast = useToastStore((s) => s.push);

  const [mnemonic, setMnemonic] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(AUTO_HIDE_SECONDS);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!mnemonic) return;
    setSecondsLeft(AUTO_HIDE_SECONDS);
    const interval = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(interval);
          setMnemonic(null);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [mnemonic]);

  useEffect(() => {
    return () => {
      setMnemonic(null);
    };
  }, []);

  async function reveal(pin: string) {
    setError(null);
    const wallet = wallets[0];
    if (!wallet) {
      navigate('/welcome');
      return;
    }
    let key: Uint8Array = new Uint8Array(0);
    let plaintext: Uint8Array = new Uint8Array(0);
    try {
      const salt = hexToBytes(wallet.salt);
      const nonce = hexToBytes(wallet.nonce);
      const ciphertext = hexToBytes(wallet.ciphertext);
      key = await deriveKeyFromPin(pin, salt);
      plaintext = decrypt(key, nonce, ciphertext);
      setMnemonic(new TextDecoder().decode(plaintext));
    } catch {
      setError(t('errors.crypto.decryptFailed'));
    } finally {
      zeroize(key, plaintext);
    }
  }

  async function handleCopy() {
    if (!mnemonic) return;
    try {
      await copySecretWithAutoClear(mnemonic);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      pushToast(t('common.copyFailed'), 'error');
    }
  }

  if (mnemonic) {
    return (
      <div className="flex min-h-screen flex-col bg-bg px-5 pb-6 pt-3 text-text-primary">
        <div className="flex items-center justify-between py-2">
          <button
            type="button"
            onClick={() => {
              setMnemonic(null);
              navigate('/settings');
            }}
            className="text-link text-[15px]"
          >
            ‹ {t('common.back')}
          </button>
          <div className="text-[16px] font-semibold text-text-primary">{t('showMnemonic.navTitle')}</div>
          <div style={{ width: 36 }} />
        </div>
        <div className="mt-4 flex flex-1 flex-col gap-3.5">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[1.4px] text-text-tertiary">
              {t('showMnemonic.recoveryPhraseLabel')}
            </div>
            <div className="mt-0.5 text-[15px] font-semibold text-text-primary">
              {t('showMnemonic.hideIn', { seconds: secondsLeft })}
            </div>
          </div>

          <div
            className="rounded-r-xl bg-surface-1 px-3.5 py-2.5"
            style={{ borderLeft: '3px solid var(--color-neg)' }}
          >
            <div className="text-[10px] font-semibold uppercase tracking-[1.4px] text-neg">
              {t('showMnemonic.keepPrivate')}
            </div>
            <div className="mt-0.5 text-[11px] leading-snug text-text-secondary">
              {t('showMnemonic.warning')}
            </div>
          </div>

          <MnemonicGrid mnemonic={mnemonic} />

          <div className="mt-auto flex flex-col gap-2">
            <Button variant="secondary" fullWidth onClick={handleCopy} disabled={copied}>
              {copied
                ? `✓ ${t('common.copied')}`
                : `${'\u29c9'} ${t('create.copyPhrase')}`}
            </Button>
            <Button
              variant="primary"
              fullWidth
              onClick={() => {
                setMnemonic(null);
                navigate('/settings');
              }}
            >
              {t('showMnemonic.done')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-bg px-5 pb-6 pt-3 text-text-primary">
      <div className="flex items-center justify-between py-2">
        <button type="button" onClick={() => navigate('/settings')} className="text-link text-[15px]">
          ‹ {t('common.back')}
        </button>
        <div className="text-[16px] font-semibold text-text-primary">{t('showMnemonic.navTitle')}</div>
        <div style={{ width: 36 }} />
      </div>
      <div className="mt-4 flex flex-1 flex-col items-center justify-center gap-6">
        <div className="text-center">
          <div className="text-[10px] font-semibold uppercase tracking-[1.4px] text-text-tertiary">
            {t('showMnemonic.verifyIdentity')}
          </div>
          <div className="mt-3 text-[22px] font-semibold tracking-[-0.4px] text-text-primary">
            {t('showMnemonic.title')}
          </div>
          <div className="mt-3 text-[14px] leading-relaxed text-text-secondary">
            {t('showMnemonic.prompt')}
          </div>
        </div>

        <PinPad onComplete={(pin) => void reveal(pin)} />

        {error && (
          <div className="mt-2 text-center text-[11px] text-neg">{error}</div>
        )}
      </div>
    </div>
  );
}
